<script setup lang="ts">
import { useProjectStore } from '@/store/projectStore';
import { useMapStore } from '@/store/remapStore';
import { exportExcelFile } from '@/utils/helpers/excel-helper';

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const messages = useAlerts();
const projectStore = useProjectStore();
const mapStore = useMapStore();

const includeDocuments = ref(false);
const isExporting = ref(false);

async function handleExport() {
  isExporting.value = true;

  try {
    const project = projectStore.project(mapStore.mapId);
    await exportExcelFile(project, $t, includeDocuments.value);
    messages.success($t('project.exportExcelSuccess'));
    emit('close');
  } catch {
    messages.error($t('project.exportExcelError'));
  } finally {
    isExporting.value = false;
  }
}

function cancel() {
  emit('close');
}
</script>

<template>
  <UiModal :title="$t('project.exportExcel')" @close="cancel">
    <div class="main">
      <div class="export-options">
        <label class="export-option-label">
          {{ $t('project.exportOptions') }}:
        </label>

        <UiCheckboxWithLabel
          :value="includeDocuments"
          @toggle="includeDocuments = !includeDocuments"
        >
          <div>
            <div>{{ $t('project.includeDocuments') }}</div>
            <div class="checkbox-hint">
              {{ $t('project.includeDocumentsHint') }}
            </div>
          </div>
        </UiCheckboxWithLabel>
      </div>

      <UiButton
        :disabled="isExporting"
        :loading="isExporting"
        @click="handleExport"
      >
        {{ $t('project.exportExcel') }}
      </UiButton>
    </div>
  </UiModal>
</template>

<style lang="scss" scoped>
.main {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--space-12);
}

.export-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  padding: var(--space-12);

  .export-option-label {
    font-weight: var(--font-weight-bold);
    color: var(--color-gray-900);
  }
}

.checkbox-hint {
  font-size: var(--font-size-12);
  color: var(--color-gray-600);
  margin-top: var(--space-4);
}
</style>
